/**
 * CompanyFilters - Slide-out filter drawer for company list
 * Mirrors Angular company-list filter form
 * Styled to match InviteeFilters drawer pattern
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/SearchableSelect'
import { useAppSelector } from '@/app/hooks'
import { selectIsAuthenticated } from '@/features/auth/authSlice'
import { useUserRole } from '@/hooks/useUserRole'
import { getOrganizations } from '@/features/invitee/services/inviteeService'
import { getStates } from '@/lib/api/statesService'
import { cityFilterOptionLabel, dropJunkCityNames } from '@/lib/utils/cityFilterDisplay'
import { shouldRunOrganizationTypeaheadSearch } from '../utils/companyListSearchLogic'
import { Filter, X } from 'lucide-react'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'
import styles from '../styles/companyFilters.module.css'

/** Match ApplicationFilters empty vs filled trigger styling. */
function isFilterValueSelected(value: unknown): boolean {
    if (value === null || value === undefined || value === '') return false
    if (Array.isArray(value)) return value.length > 0
    return true
}

function filterSelectWrapClass(value: unknown): string {
    const filled = isFilterValueSelected(value)
    return [
        'relative',
        styles.pillFilterWrapperVertical,
        filled ? styles.pillFilterFilled : styles.pillFilterEmpty,
    ].join(' ')
}

const FILTER_TEXT_INPUT_CLASS =
    'w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 transition-all focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/15 focus:border-[rgb(21_151_255)] placeholder:text-gray-400'

function filterTextInputClass(value: unknown): string {
    return [
        FILTER_TEXT_INPUT_CLASS,
        isFilterValueSelected(value) ? 'bg-slate-50' : 'bg-white',
    ].join(' ')
}

/** Extract state-keyed city list from API response (handles wrapped or direct body). */
function extractCityDataFromResponse(response: unknown): Record<string, string[]> {
    const raw = response && typeof response === 'object' && 'data' in response
        ? (response as { data?: unknown }).data
        : response
    if (!raw || typeof raw !== 'object') return {}
    const obj = raw as Record<string, unknown>
    if (obj.cities && typeof obj.cities === 'object' && !Array.isArray(obj.cities))
        return obj.cities as Record<string, string[]>
    if (obj.data && typeof obj.data === 'object' && !Array.isArray(obj.data))
        return obj.data as Record<string, string[]>
    const entries = Object.entries(obj).filter(
        ([_, v]) => Array.isArray(v) && v.every((x: unknown) => typeof x === 'string')
    )
    return entries.length ? (Object.fromEntries(entries) as Record<string, string[]>) : {}
}

import type { CompanyFiltersProps } from '../types'

export function CompanyFilters({ filters, onFiltersChange, onReset, isOpen, onClose }: CompanyFiltersProps) {
    const isAuthenticated = useAppSelector(selectIsAuthenticated)
    const { isLibertyRole, roleName } = useUserRole()

    // Organization list for filter
    const [organizations, setOrganizations] = useState<SearchableSelectOption[]>([])
    const [loadingOrgs, setLoadingOrgs] = useState(false)
    const orgRequestIdRef = useRef(0)

    // State + City lists
    const [states, setStates] = useState<SearchableSelectOption[]>([])
    const [cities, setCities] = useState<SearchableSelectOption[]>([])

    // Load organizations (liberty roles only); stale in-flight requests are ignored.
    const loadOrganizations = useCallback(async (search: string) => {
        if (!isAuthenticated) return
        const requestId = ++orgRequestIdRef.current
        setLoadingOrgs(true)
        try {
            const data = await getOrganizations(search)
            if (requestId !== orgRequestIdRef.current) return
            const mapped = data.map(org => ({ value: String(org.org_id), label: org.org_name || String(org.org_id) }))
            setOrganizations((prev) => {
                const selectedId =
                    filters.org_id != null && filters.org_id !== '' ? String(filters.org_id) : ''
                if (!selectedId || mapped.some((o) => String(o.value) === selectedId)) return mapped
                const label =
                    (filters.org_name && String(filters.org_name)) ||
                    prev.find((o) => String(o.value) === selectedId)?.label
                if (!label) return mapped
                return [{ value: selectedId, label }, ...mapped]
            })
        } catch (e) {
            if (requestId !== orgRequestIdRef.current) return
            console.error(e)
        } finally {
            if (requestId === orgRequestIdRef.current) {
                setLoadingOrgs(false)
            }
        }
    }, [isAuthenticated, filters.org_id, filters.org_name])

    /** Angular `searchOrganization`: even-length only (empty allowed). */
    const searchOrganizations = useCallback(
        (search: string) => {
            if (!shouldRunOrganizationTypeaheadSearch(search)) return
            void loadOrganizations(search)
        },
        [loadOrganizations],
    )

    // Load states via shared statesService (handles multiple API response formats)
    useEffect(() => {
        if (!isAuthenticated) return
        const fetchStates = async () => {
            try {
                const stateList = await getStates({ zip_flag: '1' })
                const stateOptions: SearchableSelectOption[] = stateList.map((s) => ({
                    value: s.state_code || s.abbreviation || '',
                    label: s.name || s.state_name || s.state_code || s.abbreviation || '',
                })).filter((o) => o.value && o.label)
                setStates(stateOptions)
            } catch (e) { console.error('Failed to load states:', e) }
        }
        fetchStates()
    }, [isAuthenticated])

    // Load cities: when state is selected show that state's cities; when no state show all cities so city can be used anytime
    useEffect(() => {
        if (!isAuthenticated) return
        const fetchCities = async () => {
            try {
                const { default: apiClient } = await import('@/lib/api/apiClient')
                const response = await apiClient.get('/city')
                const cityData = extractCityDataFromResponse(response)
                if (filters.company_state) {
                    const stateCities = dropJunkCityNames(cityData[filters.company_state] || [])
                    setCities(
                        stateCities.map((city: string) => ({
                            value: city,
                            label: cityFilterOptionLabel(city),
                        })),
                    )
                } else {
                    const merged: string[] = []
                    Object.entries(cityData).forEach(([, list]: [string, unknown]) => {
                        (Array.isArray(list) ? list : []).forEach((city: string) => merged.push(city))
                    })
                    const uniqueSorted = Array.from(new Set(dropJunkCityNames(merged))).sort((a, b) =>
                        a.localeCompare(b),
                    )
                    setCities(
                        uniqueSorted.map((city: string) => ({
                            value: city,
                            label: cityFilterOptionLabel(city),
                        })),
                    )
                }
            } catch (e) { console.error('Failed to load cities:', e) }
        }
        fetchCities()
    }, [isAuthenticated, filters.company_state, isOpen])

    // Initial load
    useEffect(() => {
        if (isAuthenticated && isLibertyRole && organizations.length === 0) {
            loadOrganizations('')
        }
    }, [isAuthenticated, isLibertyRole])

    // Angular setDefaultRequestParam: seed org dropdown from session org_id + org_name
    useEffect(() => {
        if (!filters.org_id || !filters.org_name) return
        const id = String(filters.org_id)
        const name = String(filters.org_name)
        setOrganizations((prev) => {
            if (prev.some((o) => String(o.value) === id)) return prev
            return [{ value: id, label: name }, ...prev]
        })
    }, [filters.org_id, filters.org_name])

    // Escape to close
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        if (isOpen) window.addEventListener('keydown', handleEsc)
        return () => window.removeEventListener('keydown', handleEsc)
    }, [isOpen, onClose])

    useBodyScrollLock(isOpen)

    const showFavorites = ['ROLE_LIBERTY_SALES', 'ROLE_ADMIN'].includes(roleName || '')

    return (
        <>
            {/* Backdrop */}
            <div
                data-testid="company-filters-backdrop"
                className={`fixed inset-0 bg-black/20 backdrop-blur-sm z-[2147483646] transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                onClick={onClose}
            />

            {/* Drawer */}
            <div
                data-testid="company-filters-drawer"
                className={`fixed top-0 right-0 h-full w-full sm:max-w-sm bg-white shadow-2xl z-[2147483647] transform transition-transform duration-300 ease-in-out flex flex-col touch-scroll overflow-y-auto ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
            >

                {/* Drawer Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                    <div className="flex items-center gap-2 text-slate-800">
                        <Filter className="h-5 w-5 text-[rgb(21_151_255)]" />
                        <h2 className="text-lg font-bold">Filters</h2>
                    </div>
                    <button
                        data-testid="company-filters-close-button"
                        onClick={onClose}
                        className="p-2 -mr-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-all"
                        type="button"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Drawer Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">

                    {/* Organization */}
                    {isLibertyRole && (
                        <div className="space-y-2">
                            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Organization</label>
                            <div className={filterSelectWrapClass(filters.org_id)}>
                                <SearchableSelect
                                    triggerDataTestId="company-filters-org-trigger"
                                    placeholder="All Organizations"
                                    options={organizations}
                                    value={filters.org_id ? String(filters.org_id) : ''}
                                    onChange={(val) => {
                                        const orgId = Array.isArray(val) ? val[0] : val
                                        if (!orgId) {
                                            onFiltersChange({ org_id: undefined, org_name: undefined })
                                            return
                                        }
                                        const label = organizations.find((o) => String(o.value) === String(orgId))?.label
                                        onFiltersChange({
                                            org_id: orgId,
                                            org_name: label || undefined,
                                        })
                                    }}
                                    onSearch={searchOrganizations}
                                    onPasteSearch={loadOrganizations}
                                    immediateSearchOnPaste
                                    onOpen={() => loadOrganizations('')}
                                    searchPlaceholder="Search By Organizations Name"
                                    loading={loadingOrgs}
                                />
                            </div>
                        </div>
                    )}

                    {/* Status: Angular only shows when org_id is set (`*ngIf="filterForm.get('org_id').value"`) */}
                    {!!filters.org_id && (
                        <div className="space-y-2">
                            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</label>
                            <div className={filterSelectWrapClass(filters.listed)}>
                                <SearchableSelect
                                    triggerDataTestId="company-filters-status-trigger"
                                    placeholder="All"
                                    searchable={false}
                                    options={[
                                        { value: '', label: 'All' },
                                        { value: '1', label: 'Active' },
                                        { value: '0', label: 'Not Active' },
                                    ]}
                                    value={filters.listed === '0' ? '0' : filters.listed === '1' ? '1' : ''}
                                    onChange={(val) => {
                                        const v = Array.isArray(val) ? val[0] : val
                                        onFiltersChange({ listed: v || undefined, page: 1 })
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {/* State */}
                    <div className="space-y-2">
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">State</label>
                        <div className={filterSelectWrapClass(filters.company_state)}>
                            <SearchableSelect
                                triggerDataTestId="company-filters-state-trigger"
                                placeholder="Select State"
                                options={states}
                                value={filters.company_state || ''}
                                onChange={(val) => {
                                    const stateVal = Array.isArray(val) ? val[0] : val
                                    onFiltersChange({ company_state: stateVal || undefined, company_city: undefined })
                                }}
                            />
                        </div>
                    </div>

                    {/* City */}
                    <div className="space-y-2">
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">City</label>
                        <div className={filterSelectWrapClass(filters.company_city)}>
                            <SearchableSelect
                                triggerDataTestId="company-filters-city-trigger"
                                placeholder="Select City"
                                options={cities}
                                value={filters.company_city || ''}
                                onChange={(val) => {
                                    const cityVal = Array.isArray(val) ? val[0] : val
                                    onFiltersChange({ company_city: cityVal || undefined })
                                }}
                            />
                        </div>
                    </div>

                    {/* Liberty-only filters */}
                    {isLibertyRole && (
                        <>
                            {/* ITR Value */}
                            <div className="space-y-2">
                                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">ITR Value</label>
                                <div className={filterSelectWrapClass(filters.itr_value)}>
                                    <SearchableSelect
                                        triggerDataTestId="company-filters-itr-trigger"
                                        placeholder="All"
                                        searchable={false}
                                        options={[
                                            { value: '', label: 'All' },
                                            { value: '1', label: 'Assigned' },
                                            { value: '0', label: 'Unassigned' },
                                        ]}
                                        value={filters.itr_value || ''}
                                        onChange={(val) => {
                                            const v = Array.isArray(val) ? val[0] : val
                                            onFiltersChange({ itr_value: v || undefined, page: 1 })
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Unit Count */}
                            <div className="space-y-2">
                                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Unit Count</label>
                                <input
                                    data-testid="company-filters-unit-count-input"
                                    type="text"
                                    className={filterTextInputClass(filters.units_number)}
                                    placeholder="Enter unit count"
                                    value={filters.units_number || ''}
                                    onChange={(e) => onFiltersChange({ units_number: e.target.value || undefined })}
                                />
                            </div>

                            {/* Liberty ID */}
                            <div className="space-y-2">
                                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Liberty ID</label>
                                <input
                                    data-testid="company-filters-liberty-id-input"
                                    type="text"
                                    className={filterTextInputClass(filters.liberty_id)}
                                    placeholder="Enter Liberty ID"
                                    value={filters.liberty_id || ''}
                                    onChange={(e) => onFiltersChange({ liberty_id: e.target.value || undefined })}
                                />
                            </div>

                            {/* Check for Deleted */}
                            <div className="flex items-center gap-3">
                                <input
                                    data-testid="company-filters-check-deleted-checkbox"
                                    type="checkbox"
                                    id="check_for_delete"
                                    className="h-4 w-4 rounded border-slate-300 text-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)]"
                                    checked={!!filters.check_for_delete}
                                    onChange={(e) => onFiltersChange({ check_for_delete: e.target.checked || undefined })}
                                />
                                <label htmlFor="check_for_delete" className="text-sm text-slate-700 font-medium">
                                    Check For Deleted
                                </label>
                            </div>
                        </>
                    )}

                    {/* Favorites (ROLE_LIBERTY_SALES, ROLE_ADMIN only) */}
                    {showFavorites && (
                        <div className="flex items-center gap-3">
                            <input
                                data-testid="company-filters-favorites-checkbox"
                                type="checkbox"
                                id="favorite_companies"
                                className="h-4 w-4 rounded border-slate-300 text-[rgb(21_151_255)] focus:ring-[rgb(21_151_255)]"
                                checked={!!filters.favorite_companies}
                                onChange={(e) => onFiltersChange({ favorite_companies: e.target.checked || undefined })}
                            />
                            <label htmlFor="favorite_companies" className="text-sm text-slate-700 font-medium">
                                Favorites
                            </label>
                        </div>
                    )}
                </div>

                {/* Drawer Footer */}
                <div className="p-4 sm:p-6 border-t border-slate-100 bg-slate-50 flex flex-wrap items-center justify-between gap-4">
                    <button
                        data-testid="company-filters-reset-all-button"
                        onClick={onReset}
                        className="filter-drawer-reset-all"
                        type="button"
                    >
                        Reset All
                    </button>
                    <button
                        data-testid="company-filters-done-button"
                        onClick={onClose}
                        className={cn(primaryEntityActionButtonClass, 'flex-1')}
                        type="button"
                    >
                        Done
                    </button>
                </div>
            </div>
        </>
    )
}
